import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, Alert, Spinner, Form, Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles } from './styles/adminGallery.js';
import { useAuth } from '../../context/AuthContext';
import {
    ADMIN_GALLERY_ENDPOINT,
    ADMIN_GALLERY_ITEM_ENDPOINT,
    API_BASE_URL
} from '../../config/api';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const formatBytes = (bytes = 0) => {
    if (!bytes) {
        return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / Math.pow(1024, exponent);
    return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const AdminGallery = () => {
    const history = useHistory();
    const { user, loading: authLoading } = useAuth();

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refreshToken, setRefreshToken] = useState(0);

    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedDataUrl, setSelectedDataUrl] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadMessage, setUploadMessage] = useState('');

    const [deleting, setDeleting] = useState({});
    const fileInputRef = useRef(null);

    const isAdmin = useMemo(() => {
        if (!Array.isArray(user?.roles)) {
            return false;
        }
        return user.roles.some((role) => typeof role === 'string' && role.toLowerCase() === 'admin');
    }, [user]);

    useEffect(() => {
        if (!authLoading) {
            if (!user) {
                history.push(`${process.env.PUBLIC_URL || ''}/login`);
            }
        }
    }, [authLoading, user, history]);

    const fetchImages = useCallback(async () => {
        if (!isAdmin) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load gallery images.');
            }

            const payload = await response.json();
            setImages(Array.isArray(payload.images) ? payload.images : []);
        } catch (err) {
            setError(err.message || 'Unable to load gallery images.');
            setImages([]);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (!authLoading && isAdmin) {
            fetchImages();
        }
    }, [authLoading, isAdmin, fetchImages, refreshToken]);

    const clearSelection = () => {
        setSelectedFile(null);
        setSelectedDataUrl('');
        setPreviewUrl('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        setUploadMessage('');
        setUploadError('');

        if (!file) {
            clearSelection();
            return;
        }

        if (!file.type.startsWith('image/')) {
            setUploadError('Please choose a valid image file.');
            clearSelection();
            return;
        }

        if (file.size > MAX_UPLOAD_BYTES) {
            setUploadError('Image must be 5MB or smaller.');
            clearSelection();
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setSelectedFile(file);
            setSelectedDataUrl(typeof reader.result === 'string' ? reader.result : '');
            setPreviewUrl(typeof reader.result === 'string' ? reader.result : '');
        };
        reader.onerror = () => {
            setUploadError('Unable to read the selected file.');
            clearSelection();
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async (event) => {
        event.preventDefault();
        setUploadError('');
        setUploadMessage('');

        if (!selectedFile || !selectedDataUrl) {
            setUploadError('Please select an image to upload.');
            return;
        }

        try {
            setUploading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fileName: selectedFile.name,
                    dataUrl: selectedDataUrl
                })
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Failed to upload image.');
            }
            setUploadMessage('Image uploaded successfully.');
            clearSelection();
            setRefreshToken((prev) => prev + 1);
        } catch (err) {
            setUploadError(err.message || 'Failed to upload image.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (fileName) => {
        if (!window.confirm('Delete this image? This action cannot be undone.')) {
            return;
        }

        setDeleting((prev) => ({ ...prev, [fileName]: true }));
        setError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_GALLERY_ITEM_ENDPOINT(fileName), {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Failed to delete image.');
            }

            setRefreshToken((prev) => prev + 1);
        } catch (err) {
            setError(err.message || 'Failed to delete image.');
        } finally {
            setDeleting((prev) => {
                const copy = { ...prev };
                delete copy[fileName];
                return copy;
            });
        }
    };

    return (
        <Styles>
            <div className="main-wrapper admin-gallery-page">
                <HeaderTwo />
                <BreadcrumbBox title="Admin Gallery" />
                <section className="gallery-area">
                    <Container>
                        <Row>
                            <Col md="12">
                                <div className="card-box">
                                    {error && (
                                        <Alert variant="danger" onClose={() => setError('')} dismissible>
                                            {error}
                                        </Alert>
                                    )}
                                    {uploadMessage && (
                                        <Alert
                                            variant="success"
                                            onClose={() => setUploadMessage('')}
                                            dismissible
                                        >
                                            {uploadMessage}
                                        </Alert>
                                    )}
                                    <div className="upload-panel">
                                        {previewUrl ? (
                                            <div className="preview">
                                                <img src={previewUrl} alt="Preview" />
                                            </div>
                                        ) : (
                                            <div className="preview muted d-flex align-items-center justify-content-center">
                                                Preview will appear here.
                                            </div>
                                        )}
                                        <div className="controls">
                                            <Form.Control
                                                ref={fileInputRef}
                                                className="form-control-file"
                                                type="file"
                                                onChange={handleFileChange}
                                                accept="image/*"
                                            />
                                            <button
                                                type="button"
                                                className="btn-upload"
                                                onClick={handleUpload}
                                                disabled={uploading || !selectedDataUrl}
                                            >
                                                {uploading ? 'Uploading...' : 'Upload Image'}
                                            </button>
                                            <span className="muted">
                                                Supported formats: JPG, PNG, GIF, WEBP · Max size 5MB.
                                            </span>
                                            {uploadError && (
                                                <Alert
                                                    variant="danger"
                                                    className="w-100 mb-0"
                                                    onClose={() => setUploadError('')}
                                                    dismissible
                                                >
                                                    {uploadError}
                                                </Alert>
                                            )}
                                        </div>
                                    </div>
                                    <div className="data-grid">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <Spinner animation="border" role="status" />
                                            </div>
                                        ) : images.length === 0 ? (
                                            <Alert variant="light" className="text-center mb-0">
                                                No images uploaded yet.
                                            </Alert>
                                        ) : (
                                            <Table responsive bordered hover>
                                                <thead>
                                                    <tr>
                                                        <th>Preview</th>
                                                        <th>File Name</th>
                                                        <th>Size</th>
                                                        <th>Updated</th>
                                                        <th>Direct Link</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {images.map((image) => {
                                                        const previewSrc = `${API_BASE_URL}${image.url}`;
                                                        const updatedAt = image.updatedAt
                                                            ? new Date(image.updatedAt).toLocaleString()
                                                            : '—';
                                                        const fileSize = image.size ? formatBytes(image.size) : '—';
                                                        return (
                                                            <tr key={image.fileName}>
                                                                <td className="preview-cell">
                                                                    <img src={previewSrc} alt={image.fileName} />
                                                                </td>
                                                                <td>{image.fileName}</td>
                                                                <td>{fileSize}</td>
                                                                <td>{updatedAt}</td>
                                                                <td>
                                                                    <a
                                                                        href={previewSrc}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {previewSrc}
                                                                    </a>
                                                                </td>
                                                                <td className="actions-cell">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-delete"
                                                                        onClick={() => handleDelete(image.fileName)}
                                                                        disabled={Boolean(deleting[image.fileName])}
                                                                    >
                                                                        {deleting[image.fileName] ? 'Deleting…' : 'Delete'}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <FooterTwo />
            </div>
        </Styles>
    );
};

export default AdminGallery;
